import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  TextField,
  Flex,
  Card,
  Text,
  Box,
  Container,
} from '@radix-ui/themes';
import { useToast } from '@/hooks/use-toast';
import { EyeClosedIcon, EyeOpenIcon } from '@radix-ui/react-icons';

export default function ResetPasswordForm() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      toast({
        title: 'Invalid Link',
        description: 'This password reset link is invalid or has expired.',
        variant: 'destructive',
      });
      navigate('/forgot-password');
    }
  }, [token, navigate, toast]);

  // Sophisticated financial-themed animated background
  useEffect(() => {
    const canvas = document.getElementById(
      'reset-password-bg',
    ) as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Dark financial gradient with subtle movement
    let time = 0;
    const animate = () => {
      time += 0.005;
      const gradient = ctx.createLinearGradient(
        0,
        0,
        canvas.width,
        canvas.height,
      );

      // Dark financial color palette
      gradient.addColorStop(
        0,
        `hsl(${220 + Math.sin(time) * 10}, 25%, ${8 + Math.sin(time * 0.3) * 2}%)`,
      );
      gradient.addColorStop(
        0.3,
        `hsl(${240 + Math.cos(time * 0.4) * 8}, 30%, ${12 + Math.cos(time * 0.2) * 3}%)`,
      );
      gradient.addColorStop(
        0.7,
        `hsl(${260 + Math.sin(time * 0.6) * 12}, 35%, ${15 + Math.sin(time * 0.4) * 2}%)`,
      );
      gradient.addColorStop(
        1,
        `hsl(${280 + Math.cos(time * 0.5) * 15}, 40%, ${18 + Math.cos(time * 0.3) * 3}%)`,
      );

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please ensure both passwords are the same.',
        variant: 'destructive',
      });
      return;
    }

    if (newPassword.length < 8) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 8 characters long.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/auth/password-reset/confirm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          new_password: newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }

      setIsSuccess(true);
      toast({
        title: 'Password Reset Successful',
        description:
          'Your password has been reset. You can now log in with your new password.',
      });

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <Box
        style={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          padding: '20px',
        }}
      >
        <canvas
          id="reset-password-bg"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: -1,
          }}
        />

        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: `
              linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px),
              linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            zIndex: -1,
          }}
        />

        <Container
          size="3"
          style={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            maxWidth: '480px',
          }}
        >
          <Card
            style={{
              width: '100%',
              background: 'rgba(30, 41, 59, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              boxShadow:
                '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)',
              borderRadius: '20px',
            }}
          >
            <Flex direction="column" gap="6" p="8">
              <Flex direction="column" align="center" gap="4">
                <Box
                  style={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '18px',
                    background:
                      'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '32px',
                    color: 'white',
                    boxShadow: '0 12px 40px rgba(16, 185, 129, 0.4)',
                  }}
                >
                  ✓
                </Box>
                <Text
                  size="7"
                  weight="bold"
                  align="center"
                  style={{
                    background:
                      'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Password Reset!
                </Text>
                <Text
                  size="3"
                  style={{ color: '#e2e8f0 !important', textAlign: 'center' }}
                >
                  Your password has been successfully reset. You'll be
                  redirected to the login page shortly.
                </Text>
              </Flex>

              <Button
                type="button"
                size="4"
                onClick={() => navigate('/login')}
                style={{
                  width: '100%',
                  height: '56px',
                  borderRadius: '12px',
                  background:
                    'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  border: 'none',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#f8fafc',
                  cursor: 'pointer',
                  boxShadow: '0 12px 40px rgba(139, 92, 246, 0.4)',
                }}
              >
                Go to Login
              </Button>
            </Flex>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        padding: '20px',
      }}
    >
      <canvas
        id="reset-password-bg"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: -1,
        }}
      />

      <Box
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `
            linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px),
            linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          zIndex: -1,
        }}
      />

      <Box
        style={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '300px',
          height: '300px',
          background:
            'radial-gradient(circle, rgba(139, 92, 246, 0.03) 0%, transparent 70%)',
          borderRadius: '50%',
          zIndex: -1,
        }}
      />
      <Box
        style={{
          position: 'absolute',
          bottom: '20%',
          right: '10%',
          width: '200px',
          height: '200px',
          background:
            'radial-gradient(circle, rgba(16, 185, 129, 0.02) 0%, transparent 70%)',
          borderRadius: '50%',
          zIndex: -1,
        }}
      />

      <Container
        size="3"
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: '480px',
        }}
      >
        <Card
          style={{
            width: '100%',
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            boxShadow:
              '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)',
            borderRadius: '20px',
          }}
        >
          <Flex direction="column" gap="6" p="8">
            <Flex direction="column" align="center" gap="4">
              <Box
                style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '18px',
                  background:
                    'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px',
                  color: 'white',
                  boxShadow: '0 12px 40px rgba(139, 92, 246, 0.4)',
                }}
              >
                🔒
              </Box>
              <Text
                size="7"
                weight="bold"
                align="center"
                style={{
                  background:
                    'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Reset Password
              </Text>
              <Text
                size="3"
                style={{ color: '#e2e8f0 !important', textAlign: 'center' }}
              >
                Enter your new password below.
              </Text>
            </Flex>

            <form onSubmit={handleSubmit}>
              <Flex direction="column" gap="5">
                <Box>
                  <Text
                    size="3"
                    weight="medium"
                    mb="3"
                    as="div"
                    style={{ color: '#e2e8f0 !important' }}
                  >
                    New Password
                  </Text>
                  <Box style={{ position: 'relative' }}>
                    <TextField.Root
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      onFocus={() => setFocusedField('newPassword')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Enter your new password"
                      required
                      disabled={isLoading}
                      style={{
                        height: '52px',
                        borderRadius: '12px',
                        border:
                          focusedField === 'newPassword'
                            ? '2px solid #8b5cf6'
                            : '1px solid rgba(255, 255, 255, 0.2)',
                        transition: 'all 0.2s ease',
                        fontSize: '16px',
                        padding: '0 52px 0 16px',
                        background:
                          focusedField === 'newPassword'
                            ? 'rgba(139, 92, 246, 0.08)'
                            : 'rgba(255, 255, 255, 0.08)',
                        color: '#f1f5f9',
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="2"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        padding: '6px',
                        minWidth: 'auto',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#cbd5e1',
                      }}
                    >
                      {showNewPassword ? (
                        <EyeClosedIcon
                          style={{ width: 22, height: 22, color: '#cbd5e1' }}
                        />
                      ) : (
                        <EyeOpenIcon
                          style={{ width: 22, height: 22, color: '#cbd5e1' }}
                        />
                      )}
                    </Button>
                  </Box>
                  <Text
                    size="2"
                    style={{
                      color: '#94a3b8 !important',
                      marginTop: '8px',
                      display: 'block',
                    }}
                  >
                    Must be at least 8 characters long
                  </Text>
                </Box>

                <Box>
                  <Text
                    size="3"
                    weight="medium"
                    mb="3"
                    as="div"
                    style={{ color: '#e2e8f0 !important' }}
                  >
                    Confirm Password
                  </Text>
                  <Box style={{ position: 'relative' }}>
                    <TextField.Root
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onFocus={() => setFocusedField('confirmPassword')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Confirm your new password"
                      required
                      disabled={isLoading}
                      style={{
                        height: '52px',
                        borderRadius: '12px',
                        border:
                          focusedField === 'confirmPassword'
                            ? '2px solid #8b5cf6'
                            : '1px solid rgba(255, 255, 255, 0.2)',
                        transition: 'all 0.2s ease',
                        fontSize: '16px',
                        padding: '0 52px 0 16px',
                        background:
                          focusedField === 'confirmPassword'
                            ? 'rgba(139, 92, 246, 0.08)'
                            : 'rgba(255, 255, 255, 0.08)',
                        color: '#f1f5f9',
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="2"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        padding: '6px',
                        minWidth: 'auto',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#cbd5e1',
                      }}
                    >
                      {showConfirmPassword ? (
                        <EyeClosedIcon
                          style={{ width: 22, height: 22, color: '#cbd5e1' }}
                        />
                      ) : (
                        <EyeOpenIcon
                          style={{ width: 22, height: 22, color: '#cbd5e1' }}
                        />
                      )}
                    </Button>
                  </Box>
                </Box>

                <Button
                  type="submit"
                  size="4"
                  disabled={isLoading || !newPassword || !confirmPassword}
                  style={{
                    width: '100%',
                    height: '56px',
                    borderRadius: '12px',
                    background: isLoading
                      ? 'linear-gradient(135deg, #475569 0%, #334155 100%)'
                      : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    border: 'none',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: isLoading ? '#cbd5e1' : '#f8fafc',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: isLoading
                      ? 'none'
                      : '0 12px 40px rgba(139, 92, 246, 0.4)',
                    transform: isLoading ? 'scale(0.98)' : 'scale(1)',
                  }}
                >
                  {isLoading ? (
                    <Flex align="center" gap="3">
                      <Box
                        style={{
                          width: '18px',
                          height: '18px',
                          border: '2px solid rgba(203, 213, 225, 0.3)',
                          borderTop: '2px solid #cbd5e1',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                        }}
                      />
                      Resetting password...
                    </Flex>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </Flex>
            </form>
          </Flex>
        </Card>
      </Container>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        input::placeholder {
          color: #94a3b8 !important;
        }

        input:focus {
          outline: none;
        }

        [data-radix-text] {
          color: #e2e8f0 !important;
        }
      `}</style>
    </Box>
  );
}
